// Display Box for Damages Group Field Entries in Add Asset Modal

import { Box, Divider, Link, Stack, Typography } from "@mui/material";

import { FileDisplayList } from "@components/file-display-list";
import { formatDate } from "@utils/format-utils";
import { toTicketUrl } from "@views/asset-table/helperFunctions";

type DamagesGroupBoxProps = {
  date?: string | null;
  description: string;
  fileIds: string[];
  ticketId: number | null;
  ticketNumber: string;
};

export const DamagesGroupBox = ({
  date,
  description,
  fileIds,
  ticketId,
  ticketNumber,
}: DamagesGroupBoxProps) => {
  const ticketUrl = toTicketUrl(ticketId, window.location.origin);
  const hasTicketNumber = ticketNumber.trim() !== "";
  const ticketNumberUrl = hasTicketNumber
    ? `${window.location.origin.replace(/\/$/, "")}/tickets/${encodeURIComponent(ticketNumber.trim())}`
    : null;
  const fallbackLabel = ticketNumber || (ticketId ? `Ticket ${ticketId}` : "Ticket");

  return (
    <Box
      sx={(theme) => ({
        border: "0.5px solid",
        borderColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.16)"
            : "rgba(25, 52, 68, 0.16)",
        borderRadius: "10px",
        p: 2,
        mb: 2,
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.02)"
            : "rgba(255,255,255,0.9)",
      })}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems="stretch"
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box>
            <Typography variant="button">Description</Typography>
            <Divider sx={{ my: 0.75 }} />
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {description || "No description provided"}
            </Typography>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="button">Files</Typography>
            <Divider sx={{ my: 0.75 }} />
            {fileIds.length === 0 ? (
              <Typography variant="body2">No files uploaded</Typography>
            ) : (
              <FileDisplayList fileIds={fileIds} />
            )}
          </Box>
        </Box>

        <Stack
          sx={(theme) => ({
            width: { xs: "100%", sm: 190 },
            minWidth: { xs: "100%", sm: 190 },
            maxWidth: { xs: "100%", sm: 190 },
            flexShrink: 0,
            gap: 1,
            borderLeft: { xs: "none", sm: "1px solid" },
            borderTop: { xs: "1px solid", sm: "none" },
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.16)"
                : "rgba(25, 52, 68, 0.16)",
            pl: { xs: 0, sm: 1.5 },
            pt: { xs: 1.5, sm: 0 },
          })}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontWeight: 500 }}
            >
              Incident Date
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatDate(date)}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontWeight: 500 }}
            >
              Ticket
            </Typography>
            <Box sx={{ mt: 0.25 }}>
              {ticketUrl
                ? (
                  <Link href={ticketUrl} sx={{ fontWeight: 600 }}>
                    {fallbackLabel}
                  </Link>
                )
                : ticketNumberUrl
                  ? (
                    <Link href={ticketNumberUrl} sx={{ fontWeight: 600 }}>
                      {fallbackLabel}
                    </Link>
                  )
                  : (
                    <Typography variant="body2">
                      No ticket is associated with this report
                    </Typography>
                  )}
            </Box>
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};
